
import React, { useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { Upload, Plus } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { showErrorToast } from '@/utils/toast';

interface MediaDropzoneProps {
  onDrop: (acceptedFiles: File[]) => void;
  multiple?: boolean;
  accept?: Record<string, string[]>;
  disabled?: boolean;
  className?: string;
  isDragActive?: boolean;
  children?: React.ReactNode;
  previewFiles?: File[];
}

export const MediaDropzone = ({ 
  onDrop,
  multiple = true,
  accept,
  disabled = false,
  className = "",
  isDragActive: externalIsDragActive,
  children,
  previewFiles = []
}: MediaDropzoneProps) => {
  const isMobile = useIsMobile();

  const handleDropRejected = useCallback((rejections: FileRejection[]) => {
    console.error('[MediaDropzone] Files rejected by dropzone filter:', {
      count: rejections.length,
      files: rejections.map(r => ({
        name: r.file.name,
        type: r.file.type || '(empty MIME)',
        size: r.file.size,
        errors: r.errors.map(e => ({ code: e.code, message: e.message })),
      })),
    });

    const summary = rejections
      .map(r => `${r.file.name} (${r.errors[0]?.code ?? 'unknown'})`)
      .join(', ');
    showErrorToast(
      `We couldn't add ${summary}. iPhone photos may be in HEIC format — try saving as JPEG and uploading again.`
    );
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive: internalIsDragActive
  } = useDropzone({
    onDrop,
    onDropRejected: handleDropRejected,
    multiple,
    accept,
    disabled,
    // FIXED: Don't disable click when showing previews - always allow clicking to add more files
    noClick: false,
    noKeyboard: false,
  });

  const isDragActive = externalIsDragActive !== undefined ? externalIsDragActive : internalIsDragActive;

  const renderContent = useCallback(() => {
    if (disabled) {
      return (
        <div className="flex flex-col items-center justify-center p-4 text-gray-400">
          <Upload className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} mb-2`} />
          <p className={`${isMobile ? "text-xs" : "text-sm"} text-center`}>
            Upload disabled
          </p>
        </div>
      );
    }

    return (
      <div
        className={`flex flex-col items-center justify-center h-full p-4 text-gray-500
          ${isDragActive ? 'text-brand-primary' : ''}`}
      >
        {isDragActive ? (
          <>
            <Upload className={`${isMobile ? "h-6 w-6" : "h-8 w-8"} mb-2 animate-bounce`} />
            <p className={`${isMobile ? "text-xs" : "text-sm"} text-center font-medium`}>
              Drop media here
            </p>
          </>
        ) : (
          <>
            <Plus className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} mb-2`} />
            <p className={`${isMobile ? "text-xs" : "text-sm"} text-center`}>
              <span className="font-medium">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Images (50MB max), videos (200MB max)
            </p>
          </>
        )}
      </div>
    );
  }, [disabled, isDragActive, isMobile]);

  return (
    <div 
      {...getRootProps()} 
      className={`
        w-full h-full rounded-lg cursor-pointer border-2 border-dashed border-gray-300
        ${isDragActive ? 'bg-brand-primary/5 border-brand-primary' : 'hover:bg-gray-50'} 
        ${disabled ? 'cursor-not-allowed opacity-60' : ''}
        ${className}
      `}
    >
      <input {...getInputProps()} />
      {children || renderContent()}
    </div>
  );
};
